import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, CheckCircle, ArrowLeft, Loader2, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('initiate'); // 'initiate', 'verify', 'success', 'failed'
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoChecking, setAutoChecking] = useState(true);
  const [idNumber, setIdNumber] = useState('');
  const [userType, setUserType] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const token = localStorage.getItem('mojo_token');
  const user = JSON.parse(localStorage.getItem('mojo_user') || '{}');
  // Phase 0 — token in HttpOnly cookie; gate on profile.
  const isLoggedIn = !!user?.id;

  useEffect(() => {
    const id = searchParams.get('id_number') || user.id_number;
    const type = searchParams.get('user_type') || user.user_type;

    if (id) setIdNumber(id);
    if (type) setUserType(type);

    if (!isLoggedIn) {
      toast.error('Authentication required');
      navigate('/');
      return;
    }

    // Auto-check if user is already verified via wallet balance endpoint
    // Also sync id_number from DB if missing in localStorage
    if (token) {
      (async () => {
        try {
          const walletRes = await axios.get(
            `${API}/wallet/balance`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          // Sync user profile from DB into localStorage
          if (walletRes.data?.user_profile) {
            const stored = JSON.parse(localStorage.getItem('mojo_user') || '{}');
            const merged = { ...stored, ...walletRes.data.user_profile };
            localStorage.setItem('mojo_user', JSON.stringify(merged));
            // Update idNumber if it was missing
            if (!id && walletRes.data.user_profile.id_number) {
              setIdNumber(walletRes.data.user_profile.id_number);
            }
          }
          const vs = walletRes.data?.verification_status;
          if (vs?.verified) {
            setStep('success');
            toast.success('You are already verified!');
            const updatedUser = { ...user, biometric_verified: true };
            localStorage.setItem('mojo_user', JSON.stringify(updatedUser));
            setTimeout(() => navigate(type === 'merchant' ? '/merchant' : '/customer'), 2000);
            return;
          }
        } catch {
          // Could not check — proceed to initiate flow
        } finally {
          setAutoChecking(false);
        }
      })();
    } else {
      setAutoChecking(false);
    }
  }, []);

  const handleInitiate = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/biometric/initiate`,
        { 
          id_number: idNumber,
          return_url: window.location.origin 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'success') {
        setSessionData(response.data);
        setStep('verify');
        toast.success('Verification session created');
      }
    } catch (error) {
      let errorMsg = 'Failed to initiate verification';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(e => e.msg).join(', ');
        }
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // The proxy captures verification results directly into the DB,
      // so we just need to check the wallet endpoint for the latest status
      const walletRes = await axios.get(
        `${API}/wallet/balance`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const vs = walletRes.data?.verification_status;

      if (vs?.verified) {
        setStep('success');
        toast.success('Verification successful!');
        const updatedUser = { ...user, biometric_verified: true };
        localStorage.setItem('mojo_user', JSON.stringify(updatedUser));
        setTimeout(() => navigate(userType === 'merchant' ? '/merchant' : '/customer'), 2000);
      } else if (vs?.score !== null && vs?.score !== undefined) {
        // Verification happened but didn't meet threshold
        setVerifyResult({
          message: 'Verification was not successful. Please try again with better lighting and a clearer photo of your ID document.'
        });
        setStep('failed');
        toast.error('Verification was not successful. Please try again.');
      } else {
        toast.warning('No results found yet — please complete the face capture and ID scan in the portal above first, then click this button again.');
      }
    } catch (error) {
      let errorMsg = 'Failed to check verification status';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(e => e.msg).join(', ');
        }
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5F0] via-[#F5EDE4] to-[#F5E8D8] flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-2xl text-[#1B0C34] flex items-center gap-2">
            <Shield className="w-8 h-8 text-[#F2B10C]" />
            ID Verification
          </CardTitle>
          <CardDescription>Complete your ID verification using VirtuallVerify</CardDescription>
        </CardHeader>
        <CardContent>
          {autoChecking && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="w-12 h-12 animate-spin text-[#F2B10C]" />
              <p className="text-[#1B0C34]/70">Checking for existing verification results...</p>
            </div>
          )}

          {!autoChecking && step === 'initiate' && (
            <div className="space-y-6">
              <div className="p-4 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
                <h3 className="font-semibold text-[#1B0C34] mb-2">What you will need:</h3>
                <ul className="space-y-2 text-sm text-[#1B0C34]/80">
                  <li className="flex items-start gap-2">
                    <span className="text-[#145539] font-bold">✓</span>
                    <span>Your South African ID card or passport</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#145539] font-bold">✓</span>
                    <span>Good lighting for face capture</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#145539] font-bold">✓</span>
                    <span>Clear photo of your ID document</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#145539] font-bold">✓</span>
                    <span>Minimum 80% match score required</span>
                  </li>
                </ul>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Verification ID:</strong> {idNumber || 'Not provided'}
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate(userType === 'merchant' ? '/merchant' : '/customer')}
                  className="flex-1"
                >
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Back to Dashboard
                </Button>
                <Button
                  onClick={handleInitiate}
                  disabled={loading}
                  className="flex-1 bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                >
                  {loading ? 'Initializing...' : 'Start Verification'}
                </Button>
              </div>
            </div>
          )}

          {!autoChecking && step === 'verify' && (
            <div className="space-y-4">
              <div className="p-3 bg-[#145539]/10 border border-[#145539] rounded-lg">
                <p className="text-sm text-[#145539] font-medium">
                  ✓ Session ready — complete your face capture and ID scan below
                </p>
              </div>

              {/* VirtuallVerify portal via same-origin proxy */}
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <iframe
                  src={`${BACKEND_URL}${sessionData?.verification_url}`}
                  width="100%"
                  height="600"
                  allow="camera; microphone"
                  title="ID Verification"
                  className="block"
                />
              </div>
              <p className="text-xs text-center text-[#1B0C34]/50">
                If the verification portal does not load above,{' '}
                <a
                  href={`${BACKEND_URL}${sessionData?.verification_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-[#145539]"
                >
                  open it in a new tab
                </a>
                , then return here and click Check Verification Status.
              </p>

              <Button
                onClick={handleComplete}
                disabled={loading}
                className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
                size="lg"
              >
                {loading
                  ? <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Checking...</>
                  : <><CheckCircle className="mr-2 w-4 h-4" /> Check Verification Status</>}
              </Button>
              <p className="text-xs text-center text-[#1B0C34]/60">
                Complete the face capture above, then click to check your result.
              </p>

              <Button
                variant="outline"
                onClick={() => navigate(userType === 'merchant' ? '/merchant' : '/customer')}
                className="w-full"
              >
                Cancel & Return
              </Button>
            </div>
          )}

          {!autoChecking && step === 'failed' && (
            <div className="space-y-6">
              <div className="flex flex-col items-center text-center space-y-3">
                <XCircle className="w-16 h-16 text-red-500" />
                <h3 className="text-xl font-bold text-[#1B0C34]">Verification Unsuccessful</h3>
                <p className="text-[#1B0C34]/70">
                  {verifyResult?.vv_result || verifyResult?.message || 'Your verification did not meet the required criteria.'}
                </p>
              </div>

              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
                <p className="text-sm text-red-800">
                  {verifyResult?.message || 'Your verification did not meet the required criteria.'}
                </p>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  You can try the verification process again. Ensure you have good lighting and a clear photo of your ID document.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate(userType === 'merchant' ? '/merchant' : '/customer')}
                  className="flex-1"
                >
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Back to Dashboard
                </Button>
                <Button
                  onClick={() => { setVerifyResult(null); setStep('initiate'); }}
                  className="flex-1 bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                >
                  <RefreshCw className="mr-2 w-4 h-4" />
                  Try Again
                </Button>
              </div>
            </div>
          )}

          {!autoChecking && step === 'success' && (
            <div className="space-y-6 text-center">
              <div className="flex justify-center">
                <CheckCircle className="w-24 h-24 text-[#145539]" />
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-[#1B0C34] mb-2">Verification Successful!</h3>
                <p className="text-[#1B0C34]/70">
                  Your ID has been verified. You can now perform all transactions.
                </p>
              </div>

              <div className="p-4 bg-[#145539]/10 border border-[#145539] rounded-lg">
                <p className="text-sm text-[#145539]">
                  Redirecting to dashboard in 3 seconds...
                </p>
              </div>

              <Button
                onClick={() => navigate(userType === 'merchant' ? '/merchant' : '/customer')}
                className="bg-[#145539] hover:bg-[#1e7049] text-white"
              >
                Go to Dashboard Now
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyPage;
